<script setup lang="ts">
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';

interface RecurringNeeds {
  month1Amount: number | null;
  month2Amount: number | null;
  month3Amount: number | null;
}

interface DefaultDates {
  month1: Date;
  month2: Date;
  month3: Date;
}

interface Props {
  defaultDates: DefaultDates;
  formatDateForDisplay: (date: Date) => string;
}

defineProps<Props>();

const recurringNeeds = defineModel<RecurringNeeds>({ required: true });
</script>

<template>
  <div :class="$style.recurringInputsContainer">
    <div :class="$style.label">
      What are your estimated needs over the next 3 months?
    </div>

    <div :class="$style.recurringInputs">
      <div :class="$style.recurringRow">
        <div :class="$style.inputGroup">
          <div :class="[$style.inputGroupAddon, $style.inputGroupAddonLeft]">
            <i class="pi pi-calendar"></i>
          </div>
          <InputText
            :value="formatDateForDisplay(defaultDates.month1)"
            disabled
            :class="$style.dateInputField"
          />
        </div>
        <div :class="$style.inputGroup">
          <div :class="[$style.inputGroupAddon, $style.inputGroupAddonLeft]">
            $
          </div>
          <InputNumber
            v-model="recurringNeeds.month1Amount"
            :min="0"
            :class="$style.numberInputField"
          />
          <div :class="[$style.inputGroupAddon, $style.inputGroupAddonRight]">
            .00
          </div>
        </div>
      </div>

      <div :class="$style.recurringRow">
        <div :class="$style.inputGroup">
          <div :class="[$style.inputGroupAddon, $style.inputGroupAddonLeft]">
            <i class="pi pi-calendar"></i>
          </div>
          <InputText
            :value="formatDateForDisplay(defaultDates.month2)"
            disabled
            :class="$style.dateInputField"
          />
        </div>
        <div :class="$style.inputGroup">
          <div :class="[$style.inputGroupAddon, $style.inputGroupAddonLeft]">
            $
          </div>
          <InputNumber
            v-model="recurringNeeds.month2Amount"
            :min="0"
            :class="$style.numberInputField"
          />
          <div :class="[$style.inputGroupAddon, $style.inputGroupAddonRight]">
            .00
          </div>
        </div>
      </div>

      <div :class="$style.recurringRow">
        <div :class="$style.inputGroup">
          <div :class="[$style.inputGroupAddon, $style.inputGroupAddonLeft]">
            <i class="pi pi-calendar"></i>
          </div>
          <InputText
            :value="formatDateForDisplay(defaultDates.month3)"
            disabled
            :class="$style.dateInputField"
          />
        </div>
        <div :class="$style.inputGroup">
          <div :class="[$style.inputGroupAddon, $style.inputGroupAddonLeft]">
            $
          </div>
          <InputNumber
            v-model="recurringNeeds.month3Amount"
            :min="0"
            :class="$style.numberInputField"
          />
          <div :class="[$style.inputGroupAddon, $style.inputGroupAddonRight]">
            .00
          </div>
        </div>
      </div>
    </div>

    <p :class="$style.helpText">
      If you are unsure, simply skip these fields and a Founder Success team
      member will assist you.
    </p>
  </div>
</template>

<style lang="less" module>
.recurringInputsContainer {
  border-left: 1px solid #e8e8ea;
  padding: 10px 0 10px 16px;
  margin-bottom: 6px;

  @media only screen and (max-width: 600px) {
    border-left: none;
    padding-left: 0;
  }
}

.label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 12px;
}

.recurringInputs {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.recurringRow {
  display: flex;
  gap: 12px;
  align-items: stretch;

  @media screen and (max-width: 500px) {
    flex-direction: column;
  }
}

.inputGroup {
  display: flex;
  height: 38px;
  max-width: 100%;

  .dateInputField {
    background: rgba(243, 244, 246, 1);
    border-radius: 0 6px 6px 0;
    font-size: 14px;
  }

  input {
    border-radius: 0;
  }
}

.inputGroupAddon {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background-color: #f3f4f6;
  color: #6b7280;
  font-size: 14px;
  border-top: 1px solid #d1d5db;
  border-bottom: 1px solid #d1d5db;

  i {
    font-size: 14px;
  }
}

.inputGroupAddonLeft {
  border-left: 1px solid #d1d5db;
  border-radius: 6px 0 0 6px;
}

.inputGroupAddonRight {
  border-right: 1px solid #d1d5db;
  border-radius: 0 6px 6px 0;
}

.helpText {
  font-size: 12px;
  color: #6b7280;
  margin-top: 8px;
  margin-bottom: 0;
}

.error {
  color: #ef4444;
  font-size: 12px;
  margin-top: 6px;
}
</style>
